/**
 * Rhythmz — Scene: Plasma Grid
 * ──────────────────────────────────
 * A 3D lattice of glowing nodes connected by lines. Nodes pulse with bass,
 * lines glow on beat. Slowly rotates.
 *
 * Audio: Bass → node pulse + line glow. Beat → flash.
 */

import * as THREE from 'three/webgpu';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface GridUniforms { uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N; uCol0: N; uCol1: N; uCol2: N; }

export function createPlasmaGrid(u: GridUniforms): SceneHandle {
  const group = new THREE.Group();

  // ── Build a 3D cube grid of nodes ──
  const gridSize = 5; // 5×5×5 = 125 nodes
  const spacing = 0.35;
  const positions: THREE.Vector3[] = [];

  for (let x = 0; x < gridSize; x++) {
    for (let y = 0; y < gridSize; y++) {
      for (let z = 0; z < gridSize; z++) {
        const px = (x - (gridSize - 1) / 2) * spacing;
        const py = (y - (gridSize - 1) / 2) * spacing;
        const pz = (z - (gridSize - 1) / 2) * spacing;
        // Skip center to make it hollow-ish
        const dist = Math.sqrt(px * px + py * py + pz * pz);
        if (dist > 0.15 && dist < 1.6) {
          positions.push(new THREE.Vector3(px, py, pz));
        }
      }
    }
  }

  const nodeCount = positions.length;

  // ── Node spheres (instanced) ──
  const nodeGeo = new THREE.SphereGeometry(0.06, 16, 16);
  const nodeMat = new THREE.MeshPhysicalNodeMaterial({
    metalness: 0.0,
    roughness: 0.0,
    emissive: new THREE.Color(0.3, 0.5, 1.0),
    emissiveIntensity: 1.5,
  });
  const nodes = new THREE.InstancedMesh(nodeGeo, nodeMat, nodeCount);
  nodes.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(nodes);

  // ── Lines (edges connecting nearby nodes) ──
  const linePositions: number[] = [];
  const connectDist = spacing * 1.1;

  for (let i = 0; i < nodeCount; i++) {
    for (let j = i + 1; j < nodeCount; j++) {
      const dx = positions[i].x - positions[j].x;
      const dy = positions[i].y - positions[j].y;
      const dz = positions[i].z - positions[j].z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < connectDist) {
        linePositions.push(
          positions[i].x, positions[i].y, positions[i].z,
          positions[j].x, positions[j].y, positions[j].z,
        );
      }
    }
  }

  const lineGeo = new THREE.BufferGeometry();
  lineGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(linePositions), 3));

  const lineMat = new THREE.LineBasicMaterial({
    transparent: true,
    opacity: 0.4,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  const lines = new THREE.LineSegments(lineGeo, lineMat);
  group.add(lines);

  // ── Center glow ──
  const centerGeo = new THREE.IcosahedronGeometry(0.12, 2);
  const centerMat = new THREE.MeshBasicNodeMaterial({ color: 0x4488ff });
  const center = new THREE.Mesh(centerGeo, centerMat);
  group.add(center);

  let t = 0;
  const pivot = new THREE.Object3D();

  const update = (dt: number): void => {
    t += dt;
    const bass = u.uBass.value ?? 0;
    const beat = u.uBeat.value ?? 0;

    // Color cycles
    const hueShift = t * 0.05 % 1;
    const nodeColor = new THREE.Color().setHSL(hueShift, 1, 0.6 + beat * 0.3);
    nodeMat.emissive.copy(nodeColor);
    nodeMat.emissiveIntensity = 1 + beat * 3;

    // Node positions — pulse with bass
    for (let i = 0; i < nodeCount; i++) {
      const pos = positions[i];
      const r = Math.sqrt(pos.x * pos.x + pos.y * pos.y + pos.z * pos.z);
      const pulse = 1 + bass * 0.15 * Math.sin(r * 5 + t * 2);
      pivot.position.set(pos.x * pulse, pos.y * pulse, pos.z * pulse);
      const s = 1 + bass * 0.3 + beat * 0.5;
      pivot.scale.setScalar(s);
      pivot.rotation.set(t + i, t + i * 0.7, 0);
      pivot.updateMatrix();
      nodes.setMatrixAt(i, pivot.matrix);
    }
    nodes.instanceMatrix.needsUpdate = true;

    // Lines color + opacity
    const lineCol = new THREE.Color().setHSL((hueShift + 0.3) % 1, 1, 0.5);
    lineMat.color.copy(lineCol);
    lineMat.opacity = 0.15 + bass * 0.3 + beat * 0.3;

    // Center
    center.scale.setScalar(1 + beat * 0.8);

    group.rotation.y = t * 0.08;
    group.rotation.x = Math.sin(t * 0.04) * 0.15;
  };

  const dispose = (): void => {
    nodeGeo.dispose(); nodeMat.dispose();
    lineGeo.dispose(); lineMat.dispose();
    centerGeo.dispose(); centerMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Plasma Grid' };
}